import React from 'react';
import styled from 'styled-components';
import { CANTONS } from '../../utils/cantons';
import { CURRENCIES } from '../../utils/seasonFormUtils';

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  &.fullWidth {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: ${(p) => (p.$dense ? '0.5rem 0.75rem' : '0.75rem')};
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${(p) => (p.$dense ? '0.5rem 0.75rem' : '0.75rem')};
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const SectionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 0.75rem;
  margin-top: 1rem;
  &:first-child {
    margin-top: 0;
  }
`;

const RegionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.5rem;
  padding: 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const CantonChip = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 2px solid ${(p) => (p.$selected ? '#043663' : '#e5e7eb')};
  background: ${(p) => (p.$selected ? '#eff6ff' : '#f9fafb')};
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${(p) => (p.$selected ? '#043663' : '#374151')};
  transition: all 0.15s ease;
  &:hover {
    border-color: #93c5fd;
    background: #eff6ff;
  }
  input {
    margin: 0;
    accent-color: #043663;
  }
`;

const RegionsSummary = styled.span`
  font-size: 0.8125rem;
  color: #6b7280;
  margin-left: auto;
`;

const RegionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
`;

const RegionLink = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`;

const CampaignActivateHint = styled.p`
  margin: 0 0 0.75rem 0;
  font-size: 0.8125rem;
  color: #6b7280;
`;

/**
 * Phase fields matching Settings → Tournaments create/edit (standalone).
 * When showActivateNow, includes season create behavior (draft vs upcoming/active).
 */
export default function SeasonFormFieldsShared({ form, setForm, showActivateNow = false, dense = false }) {
  const toggleRegion = (code) => {
    setForm((prev) => ({
      ...prev,
      regions_allowed: prev.regions_allowed.includes(code)
        ? prev.regions_allowed.filter((c) => c !== code)
        : [...prev.regions_allowed, code],
    }));
  };

  const selectAllRegions = () => {
    setForm((prev) => ({ ...prev, regions_allowed: CANTONS.map((c) => c.code) }));
  };
  const clearRegions = () => {
    setForm((prev) => ({ ...prev, regions_allowed: [] }));
  };

  const patch = (partial) => setForm((prev) => ({ ...prev, ...partial }));

  return (
    <FormGrid>
      <FormGroup>
        <Label>Phase name *</Label>
        <Input
          $dense={dense}
          value={form.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder="e.g. Winter 2026"
        />
      </FormGroup>
      <FormGroup className="fullWidth" />
      <FormGroup>
        <Label>Phase start (date & time) *</Label>
        <Input
          $dense={dense}
          type="datetime-local"
          value={form.start_date}
          onChange={(e) => patch({ start_date: e.target.value })}
          step="60"
        />
      </FormGroup>
      <FormGroup>
        <Label>Phase end (date & time) *</Label>
        <Input
          $dense={dense}
          type="datetime-local"
          value={form.end_date}
          onChange={(e) => patch({ end_date: e.target.value })}
          step="60"
        />
      </FormGroup>
      <FormGroup>
        <Label>Registration opens (date & time) *</Label>
        <Input
          $dense={dense}
          type="datetime-local"
          value={form.registration_start}
          onChange={(e) => patch({ registration_start: e.target.value })}
          step="60"
          placeholder="When teams can start registering"
        />
      </FormGroup>
      <FormGroup>
        <Label>Registration closes (date & time) *</Label>
        <Input
          $dense={dense}
          type="datetime-local"
          value={form.registration_end}
          onChange={(e) => patch({ registration_end: e.target.value })}
          step="60"
          placeholder="Deadline to register"
        />
      </FormGroup>
      <FormGroup>
        <Label>Registration fee (per team) *</Label>
        <Input
          $dense={dense}
          type="number"
          step="0.01"
          min="0"
          value={form.registration_fee}
          onChange={(e) => patch({ registration_fee: e.target.value })}
          placeholder="e.g. 50"
          required
        />
      </FormGroup>
      <FormGroup>
        <Label>Currency *</Label>
        <Select $dense={dense} value={form.currency} onChange={(e) => patch({ currency: e.target.value })} required>
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </FormGroup>

      {showActivateNow ? (
        <FormGroup className="fullWidth">
          <SectionLabel>Season activation</SectionLabel>
          <CampaignActivateHint>
            Matches standalone “Save as draft” vs activate: when checked, the phase is created as upcoming or active
            (from start date); when unchecked, it stays a draft in the database while still linked to this season.
          </CampaignActivateHint>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', margin: 0 }}>
            <input
              type="checkbox"
              checked={form.activate_now !== false}
              onChange={(e) => patch({ activate_now: e.target.checked })}
              style={{ marginTop: '0.25rem' }}
            />
            <span>
              <strong>Activate this phase</strong> — Uncheck to create as draft (same as standalone “Save as draft”).
            </span>
          </label>
        </FormGroup>
      ) : null}

      <FormGroup className="fullWidth">
        <SectionLabel>Score submission locking</SectionLabel>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', margin: 0 }}>
          <input
            type="checkbox"
            checked={form.score_submission_locking_enabled !== false}
            onChange={(e) => patch({ score_submission_locking_enabled: e.target.checked })}
            style={{ marginTop: '0.25rem' }}
          />
          <span>
            <strong>Unlock score submission in 28-day windows</strong> — First 28 days: 2 matches unlocked; next 28
            days: next 2 matches, etc. If unchecked, all matches stay unlocked for score submission from the start of the
            phase.
          </span>
        </label>
      </FormGroup>
      <FormGroup className="fullWidth">
        <SectionLabel>Skill level edit window (phase specific)</SectionLabel>
        <p style={{ margin: '0 0 0.5rem 0', fontSize: dense ? '0.75rem' : '0.8125rem', color: '#6b7280', lineHeight: 1.45 }}>
          Applies to <strong>new / first-time-paid players</strong> on Opening Season during this phase window
          (returning players stay locked), and to team roster skill on this phase. Players can change skill as often
          as they need until this date/time (Swiss); edits update roster skill and team average for eligible players.
          Leave empty to use the registration-close rule only.
        </p>
        <Input
          $dense={dense}
          type="datetime-local"
          value={form.skill_level_edit_deadline || ''}
          onChange={(e) => patch({ skill_level_edit_deadline: e.target.value })}
          step="60"
        />
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', marginTop: '0.75rem' }}>
          <input
            type="checkbox"
            checked={form.lock_skill_level_when_registration_closed !== false}
            onChange={(e) => patch({ lock_skill_level_when_registration_closed: e.target.checked })}
            style={{ marginTop: '0.25rem' }}
          />
          <span style={{ fontSize: dense ? '0.8125rem' : '0.875rem', lineHeight: 1.45, color: '#374151' }}>
            <strong>Stop skill level edits when registration closes</strong> — this takes priority even if the skill-level
            deadline is later. Uncheck this if you want a later deadline (for example phase starts 9 Sept, skill edits
            allowed until 21 Sept).
          </span>
        </label>
      </FormGroup>
      <FormGroup className="fullWidth">
        <SectionLabel>Regions allowed for registration</SectionLabel>
        <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8125rem', color: '#6b7280' }}>
          Select cantons that can register for this phase. Leave all unchecked to allow all regions.
        </p>
        <RegionActions>
          <RegionLink type="button" onClick={selectAllRegions}>
            Select all
          </RegionLink>
          <RegionLink type="button" onClick={clearRegions}>
            Clear
          </RegionLink>
          <RegionsSummary>
            {form.regions_allowed.length === 0
              ? 'All regions allowed'
              : `${form.regions_allowed.length} of ${CANTONS.length} selected`}
          </RegionsSummary>
        </RegionActions>
        <RegionsGrid>
          {CANTONS.map((c) => (
            <CantonChip key={c.code} $selected={form.regions_allowed.includes(c.code)}>
              <input type="checkbox" checked={form.regions_allowed.includes(c.code)} onChange={() => toggleRegion(c.code)} />
              <span>
                {c.name} ({c.code})
              </span>
            </CantonChip>
          ))}
        </RegionsGrid>
      </FormGroup>
    </FormGrid>
  );
}
